import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useGame } from '../context/GameContext';
import { ArrowLeft, Crown, Trophy, Skull, Copy, Check } from 'lucide-react';
import { AvatarChip } from '../components/Avatar';
import { CURSES, stackedTokenBonus } from '../game/curses';
import CurseInlineModal from '../components/CurseInlineModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Leaderboard() {
  const { goToMenu, setScreen, beginClassSelect, leaderboardInitialTab, setLeaderboardInitialTab } = useGame();
  const [tab, setTab] = useState(leaderboardInitialTab || 'score');
  const [entries, setEntries] = useState([]);
  const [curseBoard, setCurseBoard] = useState([]);
  const [loading, setLoading] = useState(true);

  // Clear the deep-link tab hint so a manual return to the leaderboard defaults back to SCORE
  useEffect(() => {
    if (leaderboardInitialTab) setLeaderboardInitialTab(null);
  }, []);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/leaderboard?limit=50`)
      .then(r => setEntries(r.data || []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
    // v3.11 fetch curse leaderboard alongside so the tab switch is instant
    axios.get(`${API}/curses/leaderboard?limit=25`)
      .then(r => setCurseBoard((r.data && r.data.entries) || []))
      .catch(() => setCurseBoard([]));
  }, []);

  return (
    <div className="min-h-screen w-full px-6 py-8">
      <div className="flex items-center justify-between">
        <button className="brutal-btn text-sm" onClick={goToMenu} data-testid="lb-back-button">
          <ArrowLeft size={16} /> BACK
        </button>
        <h1 className="font-display text-4xl sm:text-6xl font-black uppercase text-[color:var(--neon)]">LEADERBOARD</h1>
        <div className="w-24" />
      </div>

      {/* v3.11 tab switcher: SCORE vs CURSED */}
      <div className="mt-6 flex items-center justify-center gap-3 flex-wrap" data-testid="lb-tabs">
        <button
          className="brutal-btn text-base px-6 py-3 flex items-center gap-2 font-display font-black uppercase"
          onClick={() => setTab('score')}
          data-testid="lb-tab-score"
          style={tab === 'score'
            ? { borderColor: '#39ff14', color: '#39ff14', boxShadow: '5px 5px 0 #39ff14', background: '#061a06' }
            : { opacity: 0.55 }}
        ><Trophy size={16}/> SCORE</button>
        <button
          className="brutal-btn text-base px-6 py-3 flex items-center gap-2 font-display font-black uppercase"
          onClick={() => setTab('cursed')}
          data-testid="lb-tab-cursed"
          style={tab === 'cursed'
            ? { borderColor: '#ff007f', color: '#ff007f', boxShadow: '5px 5px 0 #ff007f', background: '#1a061a' }
            : { opacity: 0.55 }}
        ><Skull size={16}/> CURSED RUNS</button>
      </div>

      {tab === 'score' && (
        <div className="mt-6 max-w-4xl mx-auto">
          {loading && <div className="text-center text-[color:var(--text-dim)]">Loading...</div>}
          {!loading && entries.length === 0 && (
            <div className="text-center text-[color:var(--text-dim)] py-16 brutal-border-dim">
              NO SLOP YET. BE THE FIRST.
            </div>
          )}
          <div className="flex flex-col gap-2" data-testid="leaderboard-list">
            {entries.map((e, i) => (
              <div key={e.id} className={`flex items-center justify-between px-4 py-3 ${i === 0 ? 'brutal-border-pink bg-[#160a10]' : 'brutal-border-dim bg-[#0a0a0a]'}`}
                data-testid={`lb-entry-${i}`}>
                <div className="flex items-center gap-4 min-w-0">
                  <div className="font-display font-black text-2xl w-10 text-[color:var(--neon)]">#{i+1}</div>
                  {e.avatar && (e.avatar.emoji || e.avatar.color || e.avatar.drawing) && (
                    <div className="relative group" data-testid={`lb-avatar-wrapper-${i}`}>
                      <AvatarChip avatar={e.avatar} frame={e.avatar?.frame} size={38} testid={`lb-avatar-${i}`} title={e.avatar.title ? `THE ${e.avatar.title}` : undefined} />
                      {e.avatar.drawing && (
                        <div
                          className="absolute left-0 top-full mt-2 z-40 hidden group-hover:block pointer-events-none"
                          data-testid={`lb-drawing-popover-${i}`}
                        >
                          <div
                            className="brutal-border bg-[#0a0a0a] p-2"
                            style={{ borderColor: 'var(--pink)', boxShadow: '4px 4px 0 var(--pink)' }}
                          >
                            <div className="text-[9px] uppercase tracking-widest text-[color:var(--pink)] mb-1 font-mono">
                              {e.player_name}&apos;s ART
                            </div>
                            <img
                              src={e.avatar.drawing}
                              alt=""
                              className="block"
                              style={{ width: 160, height: 160, imageRendering: 'pixelated', objectFit: 'contain' }}
                              data-testid={`lb-drawing-preview-${i}`}
                              draggable={false}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-display font-black text-xl uppercase truncate">{e.player_name}</div>
                    {e.avatar?.title && (
                      <div className="text-[9px] uppercase font-mono text-[color:var(--pink)] truncate" data-testid={`lb-title-${i}`}>THE {e.avatar.title}</div>
                    )}
                  </div>
                  {e.boss_defeated && <Crown className="text-[color:var(--pink)] shrink-0" size={18} />}
                </div>
                <div className="flex items-center gap-6 text-xs uppercase font-mono">
                  <span className="text-[color:var(--text-dim)]">FL <span className="text-white">{e.floor_reached}</span></span>
                  {e.best_streak > 0 && (
                    <span className="text-[color:var(--text-dim)]" data-testid={`lb-streak-${i}`}>🔥 <span className="text-[color:var(--pink)] font-bold">×{e.best_streak}</span></span>
                  )}
                  <span className="text-[color:var(--text-dim)]">CODE <span className="text-[color:var(--pink)]">{e.run_code}</span></span>
                  <span className="font-display text-2xl font-black text-[color:var(--neon)] w-24 text-right">{e.score}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'cursed' && (
        <CursedRunsBoard
          entries={curseBoard}
          beginClassSelect={beginClassSelect}
          onRefresh={async () => {
            const r = await axios.get(`${API}/curses/leaderboard?limit=25`).catch(() => ({ data: { entries: [] } }));
            setCurseBoard((r.data && r.data.entries) || []);
          }}
        />
      )}
    </div>
  );
}

// v3.11 CURSED RUNS panel — the most-stacked run codes right now.
function CursedRunsBoard({ entries, beginClassSelect, onRefresh }) {
  const { playerName } = useGame();
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [modalEntry, setModalEntry] = useState(null); // v3.12 curse-inline modal
  return (
    <div className="mt-6 max-w-4xl mx-auto" data-testid="curse-leaderboard">
      <div className="text-center text-xs uppercase tracking-widest text-[color:var(--text-dim)] mb-4">
        the most-cursed active run codes. survive one for compounding token payouts.
      </div>
      {entries.length === 0 ? (
        <div className="text-center text-[color:var(--text-dim)] py-16 brutal-border-dim" data-testid="curse-lb-empty">
          NO CURSES PLANTED YET. LOSE A RUN AND PLANT ONE.
        </div>
      ) : (
        <div className="flex flex-col gap-3" data-testid="curse-lb-list">
          {entries.map((e, i) => {
            const stackBonus = stackedTokenBonus(e.curse_ids || []);
            const runMult = 2 + Math.max(0, e.count - 1) * 0.5;
            const heat = i === 0 ? '#ff007f' : i === 1 ? '#ff6a00' : i === 2 ? '#ffd700' : '#666';
            const isCopied = copiedIdx === i;
            const copy = async () => {
              try { await navigator.clipboard.writeText(e.run_code); setCopiedIdx(i); setTimeout(() => setCopiedIdx(null), 1400); } catch (err) { /* clipboard perms denied */ }
            };
            return (
              <div
                key={e.run_code}
                className="brutal-border-dim bg-[#0a0a0a] p-4 flex items-start gap-4 flex-wrap"
                style={{ borderColor: heat, boxShadow: `4px 4px 0 ${heat}` }}
                data-testid={`curse-lb-entry-${i}`}
              >
                <div className="flex items-center gap-4 min-w-0 flex-1">
                  <div className="font-display font-black text-3xl" style={{ color: heat, width: '3rem' }} data-testid={`curse-lb-rank-${i}`}>#{i+1}</div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-black uppercase text-xl" data-testid={`curse-lb-code-${i}`}>{e.run_code}</span>
                      <button className="brutal-btn text-[10px] px-2 py-1 flex items-center gap-1"
                        onClick={copy}
                        data-testid={`curse-lb-copy-${i}`}
                        style={{ borderColor: '#00e5ff', color: '#00e5ff', boxShadow: '2px 2px 0 #00e5ff' }}
                      >{isCopied ? <><Check size={10}/> COPIED</> : <><Copy size={10}/> COPY</>}</button>
                    </div>
                    <div className="text-[10px] uppercase font-mono tracking-widest text-[color:var(--text-dim)] mt-1">
                      TOP CURSER · <span className="text-[color:var(--pink)]">{e.top_curser || 'ANON'}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1" data-testid={`curse-lb-chips-${i}`}>
                      {(() => {
                        // v3.18 STACKED CURSES: aggregate duplicates into "×N" chips.
                        const counts = e.curse_counts || {};
                        const orderedUnique = [];
                        (e.curse_ids || []).forEach(cid => { if (!orderedUnique.includes(cid)) orderedUnique.push(cid); });
                        return orderedUnique.map((cid, ci) => {
                          const c = CURSES[cid];
                          if (!c) return null;
                          const n = counts[cid] || 1;
                          return (
                            <span
                              key={ci}
                              className="text-[9px] uppercase font-bold px-1.5 py-0.5"
                              style={{ border: `1px solid ${c.color}`, color: c.color, background: `${c.color}18` }}
                              title={`${c.name} · ${c.desc} · stacked ×${n}`}
                            >{c.art} {c.name}{n > 1 && <b> ×{n}</b>}</span>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 text-right">
                  <div className="font-display text-3xl font-black" style={{ color: heat }} data-testid={`curse-lb-count-${i}`}>
                    ×{e.count}
                  </div>
                  <div className="text-[10px] uppercase font-mono tracking-widest text-[color:var(--text-dim)]">
                    STACK · {runMult}× on win
                  </div>
                  <div className="text-[10px] uppercase font-mono text-[color:var(--neon)]" data-testid={`curse-lb-bonus-${i}`}>
                    +{stackBonus} bonus tokens
                  </div>
                  <button
                    className="brutal-btn text-xs mt-1"
                    style={{ borderColor: '#ff007f', color: '#ff007f', boxShadow: '3px 3px 0 #ff007f' }}
                    onClick={() => beginClassSelect(e.run_code, null, e.run_code)}
                    data-testid={`curse-lb-take-${i}`}
                  >
                    <Skull size={12}/> TAKE THE RUN
                  </button>
                  {/* v3.12 stack more curses without playing */}
                  <button
                    className="brutal-btn text-xs mt-1"
                    style={{ borderColor: '#ffd700', color: '#ffd700', boxShadow: '3px 3px 0 #ffd700' }}
                    onClick={() => setModalEntry(e)}
                    data-testid={`curse-lb-curse-${i}`}
                  >
                    <Skull size={12}/> + CURSE
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {modalEntry && (
        <CurseInlineModal
          runCode={modalEntry.run_code}
          playerTag={playerName || 'ANON'}
          existingCurseIds={modalEntry.curse_ids || []}
          onClose={() => setModalEntry(null)}
          onDone={() => { if (onRefresh) onRefresh(); }}
        />
      )}
    </div>
  );
}
